import { useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCheck,
  CircleX,
  Download,
  ExternalLink,
  FileUp,
  Pencil,
  Plus,
  SearchX,
  Table2,
  Trash2,
} from "lucide-react";

import { DataTable } from "@/components/DataTable";
import { SearchBar } from "@/components/SearchBar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { FlowTagsMenu } from "@/components/Tags/FlowTagsMenu";
import { FlowTagsStack } from "@/components/Tags/FlowTagsStack";
import {
  FlowTagActionsDialog,
  TagAction,
} from "@/components/Tags/FlowTagActionsDialog";
import { DeleteDialog } from "@/components/Modals/DeleteDialog";
import { EntityDialogTitle } from "@/components/Modals/EntityDialogTitle";
import { SpotToast } from "@/components/SpotToast";
import { TablesService, TableWithCount } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useTablesListState, type TablesListView } from "./tablesListView";
import {
  useTablesListTools,
  type TablesListDeps,
} from "@/lib/genie/modules/tables";
import { useSpotUser } from "@/utils/spotUser";
import { usePermissions } from "@/hooks/usePermissions";
import { useDialogMutationLoading } from "@/hooks/useDialogMutationLoading";
import { useExportTableCsv } from "@/hooks/useExportTableCsv";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";
import { timeAgo } from "@/utils/generic";
import { FileTypes, getAcceptAttributeValue } from "@/utils/mimetypes";
import { withinImportLimit } from "@/utils/csv";
import { EditTableDialog } from "./EditTableDialog";
import { useSetAtom } from "jotai";
import { importCsvFileAtom, isCreateTableOpenAtom } from "@/store/ui-atoms";

export type TableSource = "custom" | "platform";

function initialPageSize(defaultPageSize?: number): number {
  if (defaultPageSize) return defaultPageSize;
  const h = typeof window !== "undefined" ? window.innerHeight : 0;
  if (h && h < 900) return 10;
  if (h && h < 1100) return 15;
  return 20;
}

function filterTables(
  tables: TableWithCount[],
  search: string,
  tagIds: string[]
): TableWithCount[] {
  const byTags = tagIds.length
    ? tables.filter((t) => t.tags?.some((tag) => tagIds.includes(tag.id)))
    : tables;
  const q = search.trim().toLowerCase();
  return q ? byTags.filter((t) => t.name.toLowerCase().includes(q)) : byTags;
}

export function TablesList({
  defaultPageSize,
  ignoreIds,
  readOnly,
  source: sourceProp,
  onSourceChange,
}: {
  defaultPageSize?: number;
  /** Tables already open in a tab, hidden from the list. */
  ignoreIds?: string[];
  readOnly?: boolean;
  source?: TableSource;
  onSourceChange?: (source: TableSource) => void;
}) {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEditGroup = hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group");
  const [listView, changeList, readList] = useTablesListState(
    (): TablesListView => ({
      search: "",
      tagIds: [],
      sorting: [],
      page: 1,
      pageSize: initialPageSize(defaultPageSize),
    })
  );
  const {
    search,
    tagIds: selectedTags,
    sorting,
    page: currentPage,
    pageSize,
  } = listView;
  const [localSource, setLocalSource] = useState<TableSource>("custom");
  const source = sourceProp ?? localSource;
  const setSource = onSourceChange ?? setLocalSource;
  const [tagAction, setTagAction] = useState<TagAction>();
  const [deleteTarget, setDeleteTarget] = useState<TableWithCount | null>(null);
  const [editTarget, setEditTarget] = useState<TableWithCount | null>(null);
  // The dialog itself is mounted once in the dashboard layout.
  const setCreateOpen = useSetAtom(isCreateTableOpenAtom);
  const setImportCsvFile = useSetAtom(importCsvFileAtom);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Platform tables are read-only views, never editable from the list.
  const canEdit = canEditGroup && source === "custom";

  const { mutate: exportCsv } = useExportTableCsv();

  const { data, isLoading, isError } = useRemoteData(DATA_KEYS.TABLES, {
    path: { group_id: spotGroup.id },
  });

  const { data: tagsData } = useRemoteData(DATA_KEYS.TAGS, {
    path: { group_id: spotGroup.id },
  });

  const deleteMutation = useMutation({
    mutationFn: TablesService.deleteTable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.TABLES] });
      SpotToast.success("Table deleted");
      setDeleteTarget(null);
    },
    onError: () => SpotToast.error("Failed to delete table"),
  });
  const isDeleting = useDialogMutationLoading(
    deleteTarget !== null,
    deleteMutation
  );

  const bySource = useMemo(
    () => (data ?? []).filter((t) => (t.source ?? "custom") === source),
    [data, source]
  );

  const visible = useMemo(
    () => bySource.filter((t) => !ignoreIds?.includes(t.id)),
    [bySource, ignoreIds]
  );

  const filtered = useMemo(
    () => filterTables(visible, search, selectedTags),
    [visible, search, selectedTags]
  );

  const genieList = useMemo<TablesListDeps>(
    () => ({
      // Platform tables have no tags, and their list has no tag filter.
      tags:
        source === "custom"
          ? (tagsData ?? []).map((t) => ({ id: t.id, name: t.name }))
          : [],
      setView: (change) =>
        changeList({
          ...(change.search !== undefined && { search: change.search }),
          ...(change.tagIds !== undefined && { tagIds: change.tagIds }),
          ...(change.sort !== undefined && {
            sorting: change.sort
              ? [
                  {
                    id: change.sort.column,
                    desc: change.sort.direction === "desc",
                  },
                ]
              : [],
          }),
        }),
      // Read the list as last changed: Genie sets it and counts in one step.
      countTables: (change) => {
        const now = readList();
        return filterTables(
          visible,
          change.search ?? now.search,
          change.tagIds ?? now.tagIds
        ).length;
      },
    }),
    [tagsData, source, visible, changeList, readList]
  );
  // The Workshop's list, not the read-only picker in the open dialog.
  useTablesListTools(genieList, !readOnly);

  // Sort the full filtered set client-side, then paginate — DataTable only
  // renders the current page's slice, so sorting must happen before slicing.
  const sorted = useMemo(() => {
    if (sorting.length === 0) return filtered;
    const { id, desc } = sorting[0];
    const key = (t: TableWithCount): string | number => {
      switch (id) {
        case "name":
          return t.name.toLowerCase();
        case "columns":
          return t.columns.length;
        case "rows":
          return t.row_count;
        case "updated_at":
          return t.updated_at ? new Date(t.updated_at).getTime() : 0;
        default:
          return 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const av = key(a);
      const bv = key(b);
      if (av < bv) return desc ? 1 : -1;
      if (av > bv) return desc ? -1 : 1;
      return 0;
    });
  }, [filtered, sorting]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const page = Math.min(currentPage, totalPages);
  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  );

  const hasNoTables = !isLoading && bySource.length === 0;
  const allIgnored = !isLoading && bySource.length > 0 && visible.length === 0;

  const empty = isError ? (
    <NoxusEmptyBlock
      size="sm"
      icon={CircleX}
      title="Couldn't load tables"
      description="Something went wrong fetching your tables. Try refreshing the page."
    />
  ) : hasNoTables ? (
    <NoxusEmptyBlock
      size="sm"
      icon={Table2}
      title="No Tables created yet"
      description={
        canEdit
          ? "Create structured data tables your flows and agents can query with SQL and write rows to."
          : "There are currently no Tables available for this group."
      }
      button={canEdit ? "Create Table" : undefined}
      buttonIcon={Plus}
      onClick={canEdit ? () => setCreateOpen(true) : undefined}
      secondaryButton={canEdit ? "Import CSV" : undefined}
      secondaryButtonIcon={FileUp}
      secondaryOnClick={
        canEdit ? () => importInputRef.current?.click() : undefined
      }
    />
  ) : allIgnored ? (
    <NoxusEmptyBlock
      size="sm"
      icon={CheckCheck}
      title="All Tables are already open"
      description="Every Table in this workspace is already open in a tab."
    />
  ) : (
    <NoxusEmptyBlock
      size="sm"
      icon={SearchX}
      title="No tables match your filters"
      description="Try a different search term or clear the filters."
      button="Clear filters"
      buttonVariant="outline"
      onClick={() => {
        changeList({ search: "", tagIds: [] });
      }}
    />
  );

  const columns = useMemo<ColumnDef<TableWithCount, any>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <NoxusIcon icon="table" size="sm" />
            <span className="truncate text-foreground">
              {row.original.name}
            </span>
          </div>
        ),
        meta: { width: "30%", isSortable: true },
      },
      ...(source === "custom"
        ? [
            {
              id: "tags",
              header: "Tags",
              cell: ({ row }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <FlowTagsStack
                    onEditTag={(tag) => setTagAction({ type: "edit", tag })}
                    onDeleteTag={(tag) => setTagAction({ type: "delete", tag })}
                    entityId={row.original.id}
                    entityType="table"
                    entityTags={row.original.tags || []}
                    disabled={readOnly}
                  />
                </div>
              ),
              meta: { isSortable: false, width: "15%", noEllipsis: true },
            } satisfies ColumnDef<TableWithCount, any>,
          ]
        : []),
      {
        id: "columns",
        header: "Columns",
        accessorFn: (t) => t.columns.length,
        cell: ({ row }) => row.original.columns.length,
        meta: { isSortable: true },
      },
      {
        id: "rows",
        accessorKey: "row_count",
        header: "Rows",
        cell: ({ row }) => row.original.row_count,
        meta: { isSortable: true },
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        header: "Last modified",
        cell: ({ row }) =>
          row.original.updated_at ? timeAgo(row.original.updated_at) : "-",
        meta: { isSortable: true },
      },
    ],
    [source, readOnly]
  );

  return (
    <div className="flex flex-col gap-4">
      <input
        type="file"
        ref={importInputRef}
        className="hidden"
        accept={getAcceptAttributeValue([FileTypes.CSV, FileTypes.XLSX])}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && withinImportLimit(file)) setImportCsvFile(file);
          e.target.value = "";
        }}
      />
      <div className="flex items-center justify-between gap-3">
        <SearchBar
          value={search}
          setValue={(v: string) => {
            changeList({ search: v });
          }}
          placeholder="Search tables"
        />
        <div className="flex items-center gap-2 shrink-0">
          <Select
            value={source}
            onValueChange={(v) => {
              setSource(v as TableSource);
              // Platform tables are untaggable; a carried-over tag filter
              // would silently empty the list.
              changeList({ tagIds: [], page: 1 });
            }}
          >
            <SelectTrigger
              data-testid="tables-kind"
              className="w-[130px] shrink-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="custom">Custom</SelectItem>
              <SelectItem value="platform">Platform</SelectItem>
            </SelectContent>
          </Select>
          {source === "custom" && (
            <FlowTagsMenu
              tags={tagsData ?? []}
              selectedTags={selectedTags}
              setSelectedTags={(tags) =>
                changeList({
                  tagIds:
                    typeof tags === "function" ? tags(selectedTags) : tags,
                })
              }
            />
          )}
        </div>
      </div>
      <DataTable<TableWithCount>
        data={paged}
        columns={columns}
        isLoading={isLoading}
        onSort={(next) => changeList({ sorting: next })}
        sorting={sorting}
        rowHref={(t) => `/tables/${t.id}`}
        emptyState={empty}
        contextMenuItems={
          readOnly
            ? undefined
            : (t) => [
                {
                  label: "Open in New Tab",
                  icon: ExternalLink,
                  onClick: () =>
                    window.open(
                      `/tables/${t.id}`,
                      "_blank",
                      "noopener,noreferrer"
                    ),
                },
                // Platform tables are views without a CSV export endpoint.
                ...(t.source === "custom"
                  ? [
                      {
                        label: "Export CSV",
                        icon: Download,
                        onClick: () => void exportCsv(t),
                      },
                    ]
                  : []),
                ...(canEdit
                  ? [
                      {
                        label: "Edit Info",
                        icon: Pencil,
                        onClick: () => setEditTarget(t),
                      },
                      {
                        label: "Delete",
                        icon: Trash2,
                        color: "destructive",
                        onClick: () => setDeleteTarget(t),
                      },
                    ]
                  : []),
              ]
        }
        tableControls={{
          data: { items: paged, total: sorted.length, pages: totalPages },
          currentPage: page,
          setCurrentPage: (next) =>
            changeList({
              page: typeof next === "function" ? next(currentPage) : next,
            }),
          pageSize,
          onPageSizeChange: (opt) =>
            changeList({ pageSize: Number(opt.value) }),
        }}
      />
      <EditTableDialog
        table={editTarget}
        isOpen={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
      <DeleteDialog
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={
          <EntityDialogTitle
            action="Delete"
            icon="table"
            name={deleteTarget?.name ?? ""}
          />
        }
        text="Are you sure you want to delete this table? Tables are not recoverable."
        isLoading={isDeleting}
        onDelete={() => {
          if (deleteTarget)
            deleteMutation.mutate({
              path: { group_id: spotGroup.id, table_id: deleteTarget.id },
            });
        }}
      />
      <FlowTagActionsDialog
        action={tagAction}
        setAction={setTagAction}
        onDeleted={(tagId) =>
          changeList({ tagIds: selectedTags.filter((id) => id !== tagId) })
        }
      />
    </div>
  );
}
